/* global $esro */
import { debounce } from 'lodash';
import { useRef, useState, useEffect } from 'react';
import md5 from 'md5';

let basketChanged = [];
let clientDetailsChanged = [];
let globalApi = null;

window.dntEsroCustomerLogoutResult = () => {};
window.dntEsroCustomerLoginResult = () => {};
window.dntEsroEmptyCustomerResult = () => {};
window.dntEsroCustomerResult = () => {};

const refreshSessionState = debounce(() => {
  if (globalApi) {
    globalApi.refreshSessionState();
  }
}, 500);

function transformNok(nok) {
  const cleaned = nok
    .replace('Kr', '')
    .replace('kr', '')
    .replace(',', '.')
    .trim();
  return parseFloat(cleaned);
}

const initApi = () => {
  globalApi.attachEventHandler('basketChanged', e => {
    let tickets = [];
    let merchandise = [];
    if (e) {
      if (e.Tickets) {
        tickets = e.Tickets.map(ticket => {
          const timestamp = ticket.EventDateTime.replace('/Date(', '').replace(
            ')/',
            ''
          );
          const date = new Date(parseInt(timestamp));
          return {
            id: ticket.Id,
            eventId: ticket.EventId,
            hall: ticket.HallName,
            date,
            name: ticket.ShowName,
            total: ticket.TotalPriceAmount,
          };
        });
      }

      if (e.Merchandise) {
        merchandise = e.Merchandise.map(merch => {
          const id = md5(`${merch.Name}_${merch.TotalValue}`);
          return {
            id: merch.Id,
            merchandiseId: id,
            name: merch.Name,
            total: transformNok(merch.TotalValue),
          };
        });
      }
    }

    basketChanged.forEach(handler => {
      if (tickets.length || merchandise.length) {
        handler({ tickets, merchandise, sumTotal: transformNok(e.totalValue) });
      }
    });
  });

  globalApi.attachEventHandler('clientDetailsChanged', e => {
    clientDetailsChanged.forEach(handler => {
      handler(e);
    });
  });
};

const addEventListener = (eventType, handler) => {
  if (eventType == 'basketChanged') {
    basketChanged.push(handler);
  }

  if (eventType == 'clientDetailsChanged') {
    clientDetailsChanged.push(handler);
  }

  if (globalApi) {
    refreshSessionState();
  }
};

const removeEventListener = (eventType, handler) => {
  if (eventType == 'basketChanged') {
    basketChanged = basketChanged.filter(h => h !== handler);
  }

  if (eventType == 'clientDetailsChanged') {
    clientDetailsChanged = clientDetailsChanged.filter(h => h !== handler);
  }
};

const signout = () => {
  return new Promise((resolve, reject) => {
    if (globalApi) {
      window.dntEsroCustomerLogoutResult = e => {
        if (e.HasError) {
          reject({
            message: e.ErrorDescription,
          });
        } else {
          resolve();
        }
      };
      globalApi.customerLogout('dntEsroCustomerLogoutResult');
    } else {
      reject({
        message: 'Api missing',
      });
    }
  });
};

const signin = (username, password) => {
  return new Promise((resolve, reject) => {
    if (globalApi) {
      window.dntEsroCustomerLoginResult = e => {
        if (e.HasError) {
          reject({
            message: e.ErrorDescription,
          });
        } else {
          resolve();
        }
      };
      globalApi.customerLogin(username, password, 'dntEsroCustomerLoginResult');
    } else {
      reject({
        message: 'Api missing',
      });
    }
  });
};

const getEmptyCustomer = () => {
  return new Promise((resolve, reject) => {
    window.dntEsroEmptyCustomerResult = e => {
      if (e.HasError) {
        reject({
          message: e.ErrorDescription,
        });
      } else {
        resolve(e.Result);
      }
    };
    globalApi.getEmptyCustomer('dntEsroEmptyCustomerResult');
  });
};

const getCustomer = () => {
  return new Promise((resolve, reject) => {
    window.dntEsroCustomerResult = e => {
      if (e.HasError) {
        reject({
          message: e.ErrorDescription,
        });
      } else {
        resolve(e);
      }
    };
    globalApi.getCustomerDetails('dntEsroCustomerResult');
  });
};

export default () => {
  const api = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (typeof $esro === 'object') {
        clearInterval(interval);
        globalApi = $esro;

        api.current = {
          addEventListener,
          removeEventListener,
          getEmptyCustomer,
          getCustomer,
          signin,
          signout,
        };

        setReady(true);
      }
    }, 200);

    return function cleanup() {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (globalApi) {
      initApi();
      refreshSessionState();
    }
  }, [ready]);

  return api.current;
};
